const API_BASE = '/api'

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT'
  body?: unknown
}

export async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body } = options

  const response = await fetch(`${API_BASE}${endpoint}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
    body: body ? JSON.stringify(body) : undefined,
  })

  if (!response.ok) {
    // On 401, redirect to login (session expired or not authenticated)
    if (response.status === 401) {
      // Only redirect if we're not already on the login page
      if (!window.location.pathname.endsWith('/login')) {
        window.location.href = '/login'
      }
      throw new Error('Niet ingelogd of sessie verlopen')
    }

    const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
    // FastAPI validation errors return detail as an array of {loc, msg, type}
    const detail = error.detail
    const message = Array.isArray(detail)
      ? detail.map((e: { loc?: string[]; msg?: string }) =>
          e.loc ? `${e.loc.slice(-1)[0]}: ${e.msg}` : (e.msg ?? String(e))
        ).join(' | ')
      : (detail || `HTTP error ${response.status}`)
    throw new Error(message)
  }

  if (response.status === 204) {
    return undefined as T
  }

  return response.json()
}

// Auth
export interface AuthUser {
  id: number
  username: string
  is_active: boolean
  created_at: string
}

export interface LoginResponse {
  message: string
  username: string
}

export const authApi = {
  login: (username: string, password: string) =>
    request<LoginResponse>('/auth/login', { method: 'POST', body: { username, password } }),
  logout: () =>
    request<{ message: string }>('/auth/logout', { method: 'POST' }),
  me: () =>
    request<AuthUser>('/auth/me'),
}

// Dashboard
export interface DashboardStats {
  total_integrations: number
  active_integrations: number
  healthy_integrations: number
  tasks_today: number
  tasks_failed_today: number
  last_sync: string | null
}

export interface IntegrationOverview {
  id: number
  name: string
  type: string
  enabled: boolean
  last_sync_at: string | null
  last_sync_status: string | null
  is_healthy: boolean
}

export interface RecentTask {
  id: number
  task_name: string
  integration_name: string | null
  status: string
  started_at: string
  duration_seconds: number | null
}

export interface TaskHistoryItem {
  date: string
  success: number
  failed: number
  running: number
}

export interface DashboardOverview {
  dossiers: { total: number; avg_seo_score: number | null }
  scans: { failed_24h: number; running: number }
  alerts: { actionable: number; kev: number; critical: number; high: number }
  system: {
    database: boolean
    redis: boolean
    last_task_at: string | null
    last_beat_task: string | null
    minutes_since_last_task: number | null
  }
}

export const dashboardApi = {
  getOverview: () => request<DashboardOverview>('/dashboard/overview'),
  getStats: () => request<DashboardStats>('/dashboard/stats'),
  getIntegrations: () => request<IntegrationOverview[]>('/dashboard/integrations'),
  getRecentTasks: (limit = 10) => request<RecentTask[]>(`/dashboard/recent-tasks?limit=${limit}`),
  getTaskHistory: (days = 7) => request<TaskHistoryItem[]>(`/dashboard/task-history?days=${days}`),
}

// Integrations
export interface Integration {
  id: number
  name: string
  type: string
  description: string | null
  enabled: boolean
  config: Record<string, unknown>
  last_sync_at: string | null
  last_sync_status: string | null
  last_error: string | null
  created_at: string
  updated_at: string
}

export interface IntegrationType {
  type: string
  description: string
}

export interface IntegrationCreate {
  name: string
  type: string
  description?: string
  enabled?: boolean
  config?: Record<string, unknown>
}

export const integrationsApi = {
  list: (type?: string) =>
    request<Integration[]>(`/integrations/${type ? `?integration_type=${type}` : ''}`),
  get: (id: number) => request<Integration>(`/integrations/${id}`),
  create: (data: IntegrationCreate) =>
    request<Integration>('/integrations/', { method: 'POST', body: data }),
  update: (id: number, data: Partial<IntegrationCreate>) =>
    request<Integration>(`/integrations/${id}`, { method: 'PATCH', body: data }),
  delete: (id: number) =>
    request<void>(`/integrations/${id}`, { method: 'DELETE' }),
  test: (id: number) =>
    request<{ success: boolean; message: string }>(`/integrations/${id}/test`, { method: 'POST' }),
  getTypes: () => request<IntegrationType[]>('/integrations/types'),
}

// Tasks
export interface TaskLog {
  id: number
  task_id: string
  task_name: string
  integration_id: number | null
  status: string
  result: Record<string, unknown> | null
  error: string | null
  started_at: string
  completed_at: string | null
  duration_seconds: number | null
}

export interface TaskStats {
  total_tasks: number
  successful_tasks: number
  failed_tasks: number
  running_tasks: number
  avg_duration_seconds: number | null
}

export const tasksApi = {
  list: (params?: { limit?: number; offset?: number; status?: string; integration_id?: number }) => {
    const query = new URLSearchParams()
    if (params?.limit) query.set('limit', params.limit.toString())
    if (params?.offset) query.set('offset', params.offset.toString())
    if (params?.status) query.set('status', params.status)
    if (params?.integration_id) query.set('integration_id', params.integration_id.toString())
    return request<TaskLog[]>(`/tasks/?${query.toString()}`)
  },
  getStats: () => request<TaskStats>('/tasks/stats'),
  getStatus: (taskId: string) =>
    request<{ task_id: string; status: string; result: unknown }>(`/tasks/${taskId}/status`),
  triggerSync: (integrationId: number, params?: Record<string, unknown>) =>
    request<{ task_id: string; status: string }>(`/tasks/sync/${integrationId}`, {
      method: 'POST',
      body: params ? { params } : undefined,
    }),
  triggerHealthCheck: () =>
    request<{ task_id: string; status: string }>('/tasks/health-check', { method: 'POST' }),
  revoke: (taskId: string) =>
    request<{ task_id: string; status: string }>(`/tasks/${taskId}`, { method: 'DELETE' }),
}

// WooCommerce Events
export interface WooCommerceIntegration {
  id: number
  name: string
  enabled: boolean
  store_url: string
  last_sync_at: string | null
  last_sync_status: string | null
  last_error: string | null
}

export interface EventProduct {
  product_id: number
  name: string
  mep_event_id: string | null
  total_sales: number
  status: string | null
  parsed_date: string | null
}

export interface EventInvoiceLog {
  id: number
  integration_id: number
  event_id: string
  event_name: string
  event_date: string
  total_orders: number
  total_revenue: number
  invoice_sent_at: string
  invoice_email: string
  status: string
  error_message: string | null
  created_at: string
}

export interface InvoiceStats {
  total_invoices: number
  sent: number
  failed: number
  total_revenue: number
  total_orders: number
}

export interface ProcessEventResult {
  product_id: number | null
  event_id: string | null
  event_name: string | null
  event_date: string | null
  status: string
  total_orders: number | null
  total_tickets: number | null
  total_revenue: number | null
  email_sent: boolean | null
  message: string | null
  error: string | null
}

// Security Scanning
export interface SecurityScan {
  id: number
  target_url: string
  scan_type: string
  status: string
  started_at: string | null
  completed_at: string | null
  duration_seconds: number | null
  total_findings: number
  critical_count: number
  high_count: number
  medium_count: number
  low_count: number
  info_count: number
  error_message: string | null
  progress_percent: number | null
  progress_detail: string | null
  celery_task_id: string | null
  created_at: string
}

export interface SecurityFinding {
  id: number
  scan_id: number
  tool: string
  severity: string
  name: string
  description: string | null
  url: string | null
  evidence: string | null
  solution: string | null
  reference: string | null
  cwe_id: number | null
  template_id: string | null
  is_false_positive: boolean
  created_at: string
}

export interface SecurityStats {
  total_scans: number
  completed_scans: number
  failed_scans: number
  total_findings: number
  critical_count: number
  high_count: number
  medium_count: number
  low_count: number
  info_count: number
  false_positives: number
  last_scan_at: string | null
  last_scan_status: string | null
}

export interface ScanTypeInfo {
  type: string
  name: string
  description: string
  estimated_duration: string
}

export const securityApi = {
  getScanTypes: () => request<ScanTypeInfo[]>('/security/scan-types'),
  refreshIntel: () =>
    request<{ status: string; osv_task_id: string; nvd_task_id: string }>(
      '/inventory/refresh-intel',
      { method: 'POST' },
    ),
  startScan: (data: { target_url: string; scan_type: string }) =>
    request<SecurityScan>('/security/scans', { method: 'POST', body: data }),
  listScans: (params?: { status?: string; scan_type?: string; limit?: number; offset?: number }) => {
    const query = new URLSearchParams()
    if (params?.status) query.set('status', params.status)
    if (params?.scan_type) query.set('scan_type', params.scan_type)
    if (params?.limit) query.set('limit', params.limit.toString())
    if (params?.offset) query.set('offset', params.offset.toString())
    return request<SecurityScan[]>(`/security/scans?${query.toString()}`)
  },
  getScan: (id: number) => request<SecurityScan>(`/security/scans/${id}`),
  cancelScan: (id: number) =>
    request<SecurityScan>(`/security/scans/${id}/cancel`, { method: 'POST' }),
  getScanFindings: (scanId: number, params?: { severity?: string; include_false_positives?: boolean }) => {
    const query = new URLSearchParams()
    if (params?.severity) query.set('severity', params.severity)
    if (params?.include_false_positives === false) query.set('include_false_positives', 'false')
    return request<SecurityFinding[]>(`/security/scans/${scanId}/findings?${query.toString()}`)
  },
  markFalsePositive: (findingId: number, isFalsePositive: boolean) =>
    request<SecurityFinding>(`/security/findings/${findingId}`, {
      method: 'PATCH',
      body: { is_false_positive: isFalsePositive },
    }),
  getStats: () => request<SecurityStats>('/security/stats'),
}

// Monitored Targets
export interface MonitoredTarget {
  id: number
  name: string
  url: string
  host: string | null
  platform_type: string
  access_mode: string
  github_repo: string | null
  github_workflow: string | null
  tags: string[]
  scan_schedule_cron: string
  enabled: boolean
  notes: string | null
  last_inventory_at: string | null
  last_scan_at: string | null
  last_status: string | null
  health_status: string | null
  health_checked_at: string | null
  health_response_ms: number | null
  health_error: string | null
  health_consecutive_failures: number
  has_sbom_token: boolean
  created_at: string
  updated_at: string
}

export interface MonitoredTargetCreate {
  name: string
  url: string
  host?: string | null
  platform_type: string
  access_mode: string
  github_repo?: string | null
  github_workflow?: string | null
  tags?: string[]
  scan_schedule_cron?: string
  enabled?: boolean
  notes?: string | null
}

// Inventory (SBOM components + CVE's per Platformen-target)
export interface InventoryComponent {
  id: number
  name: string
  version: string
  ecosystem: string
  component_type: string
  source: string
  is_outdated: boolean | null
  latest_version: string | null
  vuln_count: number
  kev_count: number
  last_seen_at: string
}

export interface InventoryVulnerability {
  id: number
  vuln_id: string
  severity: string
  cvss_score: number | null
  kev: boolean
  epss_score: number | null
  fixed_version: string | null
  advisory_url: string | null
  description: string | null
  is_false_positive: boolean
  suppressed: boolean
  first_seen_at: string
}

export interface InventoryVulnerabilityWithComponent extends InventoryVulnerability {
  component_id: number
  component_name: string
  component_version: string
}

export interface RefreshIntelResult {
  status: string
  osv_components: number
  new_cves: number
  new_alerts: number
  kev_alerts: number
}

export type CveSeverity = 'critical' | 'high' | 'medium' | 'low' | 'unknown'

export interface ComponentExposure {
  component_id: number
  name: string
  version: string
  ecosystem: string
  component_type: string
  worst_severity: CveSeverity
  vuln_count: number
  kev: boolean
  vuln_ids: string[]
  fixed_versions: string[]
}

export interface ProductOverview {
  dossier_id: number
  name: string
  url: string | null
  last_inventory_at: string | null
  stale: boolean
  component_count: number
  os_component_count: number
  outdated_count: number
  vulnerable_component_count: number
  kev_count: number
  severity_counts: Record<CveSeverity, number>
  worst_severity: CveSeverity | null
  components: ComponentExposure[]
}

export const inventoryApi = {
  // One entry per product, worst exposure first (backend ranks them).
  getOverview: () => request<ProductOverview[]>('/inventory/overview'),
  getComponents: (dossierId: number) =>
    request<InventoryComponent[]>(`/inventory/dossiers/${dossierId}/components`),
  getVulnerabilities: (componentId: number) =>
    request<InventoryVulnerability[]>(`/inventory/components/${componentId}/vulnerabilities`),
  getAllVulnerabilities: (dossierId: number) =>
    request<InventoryVulnerabilityWithComponent[]>(`/inventory/dossiers/${dossierId}/vulnerabilities`),
  // With dossierId: the backend runs the OSV re-check in-line and returns the
  // real result — without it, refresh-intel only dispatches a background
  // task and the caller never learns the outcome (see useRefreshInventoryIntel).
  refreshIntel: (dossierId: number) =>
    request<{ status: string; result: RefreshIntelResult; nvd_task_id: string }>(
      `/inventory/refresh-intel?dossier_id=${dossierId}`,
      { method: 'POST' },
    ),
}

// Alerts
export interface SecurityAlert {
  id: number
  kind: string  // "cve" | "downtime" | "security" | "gsc"
  target_id: number | null
  dossier_id: number | null
  vuln_id_ref: number | null
  dedup_key: string
  title: string | null  // human-readable title for non-CVE alerts
  vuln_id_str: string | null
  component_name: string | null
  component_version: string | null
  severity: string
  kev: boolean
  epss_score: number | null  // raw EPSS probability 0.0–1.0 (multiply by 100 for %)
  status: string
  channel: string
  notified_at: string | null
  created_at: string
  updated_at: string
  /** Body text for non-CVE alerts (EOL, visual diff, CT-log, GSC anomaly). */
  message: string | null
  /** When the CVE was last confirmed present in the inventory. */
  last_seen_at: string | null
  reopened_count: number
  suppressed: boolean
  /** Resolved per request — the site this alert belongs to. */
  dossier_name: string | null
  /** The version that fixes this CVE, from the OSV cross-check. */
  fixed_version: string | null
  /** owner/repo of the dossier, for filing an issue. */
  github_repo: string | null
}

export interface AlertStats {
  total_new: number
  kev: number
  critical: number
  high: number
  /** Informational maintenance rows, kept out of every other count. */
  maintenance: number
  /** Critical, high or KEV — counted once per alert. Feeds the sidebar badge. */
  actionable: number
}

/** Open (new + acked) and deferred alerts of one site. dossier_id null =
 * alerts not tied to a site. */
export interface AlertSiteSummary {
  dossier_id: number | null
  name: string
  url: string | null
  github_repo: string | null
  open: number
  kev: number
  critical: number
  high: number
  deferred: number
  by_kind: Record<string, number>
  last_inventory_at: string | null
}

export interface ClaudeInstruction {
  dossier_id: number
  text: string
  packages: number
  other_alerts: number
}

export type RecheckStatus =
  | { state: 'pending' }
  | { state: 'done'; result: { new_alerts?: number; resolved_alerts?: number; vulns_found?: number } }
  | { state: 'failed'; error: string }

export const alertsApi = {
  list: (params?: {
    status?: string
    target_id?: number
    dossier_id?: number
    kind?: string
    exclude_kind?: string
    severity?: string
    kev_only?: boolean
    no_dossier?: boolean
    limit?: number
    offset?: number
  }) => {
    const query = new URLSearchParams()
    if (params?.status) query.set('status', params.status)
    if (params?.target_id) query.set('target_id', String(params.target_id))
    if (params?.dossier_id) query.set('dossier_id', String(params.dossier_id))
    if (params?.kind) query.set('kind', params.kind)
    if (params?.exclude_kind) query.set('exclude_kind', params.exclude_kind)
    if (params?.severity) query.set('severity', params.severity)
    if (params?.kev_only) query.set('kev_only', 'true')
    if (params?.no_dossier) query.set('no_dossier', 'true')
    if (params?.limit) query.set('limit', String(params.limit))
    if (params?.offset) query.set('offset', String(params.offset))
    const qs = query.toString()
    return request<SecurityAlert[]>(`/alerts/${qs ? `?${qs}` : ''}`)
  },
  getStats: () => request<AlertStats>('/alerts/stats'),
  bulkUpdateStatus: (alertIds: number[], status: string) =>
    request<{ updated: number; requested: number }>('/alerts/bulk-status', {
      method: 'PATCH',
      body: { alert_ids: alertIds, status },
    }),
  updateStatus: (id: number, status: 'new' | 'acked' | 'resolved') =>
    request<SecurityAlert>(`/alerts/${id}`, { method: 'PATCH', body: { status } }),
  getSites: () => request<AlertSiteSummary[]>('/alerts/sites'),
  getSite: (dossierId: number) => request<AlertSiteSummary>(`/alerts/sites/${dossierId}`),
  setVex: (alertIds: number[], status: 'not_affected' | 'false_positive', justification: string) =>
    request<{ updated: number; skipped: number; requested: number }>('/alerts/vex', {
      method: 'POST',
      body: { alert_ids: alertIds, status, justification },
    }),
  getClaudeInstruction: (dossierId: number) =>
    request<ClaudeInstruction>(`/alerts/sites/${dossierId}/claude-instruction`),
  recheck: (dossierId: number) =>
    request<{ task_id: string }>(`/alerts/sites/${dossierId}/recheck`, { method: 'POST' }),
  recheckStatus: (taskId: string) =>
    request<RecheckStatus>(`/alerts/recheck/${encodeURIComponent(taskId)}`),
}

// Dossiers
export interface BusinessContext {
  business_type: string | null
  service_area: string[]
  language: string | null
  word_range: [number, number] | null
}

export interface Dossier {
  id: number
  name: string
  url: string
  client_name: string | null
  gsc_property: string | null
  notes: string | null
  baseline_date: string | null
  competitors: string[]
  active: boolean
  skip_scanning: boolean
  business_context: BusinessContext | null
  last_avg_seo_score: number | null
  last_gsc_clicks: number | null
  last_gsc_impressions: number | null
  last_gsc_position: number | null
  last_sync_at: string | null
  prev_gsc_clicks: number | null
  // Names of other dossiers reading the same GSC property. Non-empty means the
  // GSC figures above are that property's, not this dossier's alone.
  gsc_property_shared_with: string[]
  // Absorbed from MonitoredTarget (migration 063). sbom_token itself is never
  // returned — only whether one is set.
  platform_type: string | null
  github_repo: string | null
  github_workflow: string | null
  has_sbom_token: boolean
  last_inventory_at: string | null
  health_status: string | null
  health_checked_at: string | null
  health_response_ms: number | null
  health_error: string | null
  last_security_critical: number | null
  last_security_high: number | null
  last_security_medium: number | null
  last_security_scan_type: string | null
  created_at: string
  updated_at: string
}

export interface DossierCreate {
  name: string
  url: string
  client_name?: string | null
  gsc_property?: string | null
  notes?: string | null
  baseline_date?: string | null
  competitors?: string[]
  business_context?: BusinessContext | null
}

export interface CompetitorEntry {
  url: string
  pillar_scores: Record<string, number> | null
  score: number | null
  scanned_at: string | null
}

export interface CompetitorComparison {
  dossier: CompetitorEntry
  competitors: CompetitorEntry[]
}

export interface ActiveScan {
  id: number
  dossier_id: number
  scan_type: string
  kind?: string
  status: string
  progress_percent: number | null
  progress_detail: string | null
}

export interface DossierLatestScores {
  dossier_id: number
  scores: Record<string, { score: number; date: string; prev: number | null }>
}

export interface GscSnapshotData {
  id: number
  dossier_id: number
  snapshot_date: string
  total_clicks: number
  total_impressions: number
  avg_ctr: number
  avg_position: number
  top_queries: GscPerformanceRow[] | null
  top_pages: GscPerformanceRow[] | null
  created_at: string
}

export const dossiersApi = {
  list: (params?: { active?: boolean }) => {
    const qs = params?.active !== undefined ? `?active=${params.active}` : ''
    return request<Dossier[]>(`/dossiers/${qs}`)
  },
  get: (id: number) => request<Dossier>(`/dossiers/${id}`),
  create: (data: DossierCreate) =>
    request<Dossier>('/dossiers/', { method: 'POST', body: data }),
  update: (id: number, data: Partial<DossierCreate> & { active?: boolean; skip_scanning?: boolean }) =>
    request<Dossier>(`/dossiers/${id}`, { method: 'PATCH', body: data }),
  delete: (id: number) => request<void>(`/dossiers/${id}`, { method: 'DELETE' }),
  // Returns the plaintext token ONCE; afterwards only has_sbom_token is exposed.
  // Rotating invalidates the previous token immediately.
  generateSbomToken: (id: number) =>
    request<{ sbom_token: string }>(`/dossiers/${id}/sbom-token`, { method: 'POST' }),
  getSnapshots: (id: number, days = 90) =>
    request<GscSnapshotData[]>(`/dossiers/${id}/snapshots?days=${days}`),
  getScans: (id: number, limit = 50) =>
    request<SeoScan[]>(`/dossiers/${id}/scans?limit=${limit}`),
  getScoreHistory: (id: number, days = 90) =>
    request<{ date: string; score: number; algo_version: number }[]>(`/dossiers/${id}/score-history?days=${days}`),
  getIndexReport: (id: number) =>
    request<IndexReport>(`/dossiers/${id}/index-report`),
  getPositionBuckets: (id: number, days = 28) =>
    request<PositionBuckets>(`/dossiers/${id}/position-buckets?days=${days}`),
  getGscTrends: (id: number) =>
    request<GscTrends>(`/dossiers/${id}/gsc-trends`),
  sync: (id: number) =>
    request<{ task_id: string; status: string }>(`/dossiers/${id}/sync`, { method: 'POST' }),
  backfillGsc: (id: number, days = 90) =>
    request<{ task_id: string; status: string; days: number }>(`/dossiers/${id}/backfill-gsc?days=${days}`, { method: 'POST' }),
  scanAll: (id: number) =>
    request<{ scan_ids: number[]; scan_types: string[] }>(`/dossiers/${id}/scan-all`, { method: 'POST' }),
  triggerScan: (id: number, scan_type: string) =>
    request<{ scan_type: string; task_id: string; scan_id?: number }>(`/dossiers/${id}/trigger-scan`, { method: 'POST', body: { scan_type } }),
  scanCompetitors: (id: number) =>
    request<{ scan_ids: number[]; competitors: number }>(`/dossiers/${id}/scan-competitors`, { method: 'POST' }),
  getCompetitorComparison: (id: number) =>
    request<CompetitorComparison>(`/dossiers/${id}/competitor-comparison`),
  batchScan: (dossier_ids: number[], scan_types: string[]) =>
    request<{ dispatched: number; scan_ids: number[]; security_scan_ids?: number[] }>(
      '/dossiers/batch-scan',
      { method: 'POST', body: { dossier_ids, scan_types } },
    ),
  getActiveScans: () => request<ActiveScan[]>('/dossiers/active-scans'),
  getLatestScores: () => request<DossierLatestScores[]>('/dossiers/latest-scores'),
  getSecurityScans: (id: number, limit = 20) =>
    request<SecurityScan[]>(`/dossiers/${id}/security-scans?limit=${limit}`),
  startSecurityScan: (id: number, scanType: string) =>
    request<SecurityScan>(`/dossiers/${id}/security-scan`, {
      method: 'POST',
      body: { scan_type: scanType },
    }),
  getSecurityFindings: (dossierId: number, scanId: number, params?: { severity?: string }) => {
    const qs = params?.severity ? `?severity=${params.severity}` : ''
    return request<SecurityFinding[]>(`/dossiers/${dossierId}/security-findings/${scanId}${qs}`)
  },
  getScanSummary: (id: number) =>
    request<ScanTypeSummary[]>(`/dossiers/${id}/scan-summary`),
  getScanSchedules: (id: number) =>
    request<ScanSchedule[]>(`/dossiers/${id}/scan-schedules`),
  updateScanSchedules: (id: number, schedules: ScanScheduleUpdate[]) =>
    request<ScanSchedule[]>(`/dossiers/${id}/scan-schedules`, { method: 'PUT', body: schedules }),
  triggerContentAudit: (id: number, page_url: string) =>
    request<{ audit_id: number; status: string; task_id: string }>(
      `/dossiers/${id}/content-audit`,
      { method: 'POST', body: { page_url } },
    ),
  triggerContentAuditBulk: (id: number) =>
    request<{ dispatched: number; skipped: number; total_pages?: number; reason?: string }>(
      `/dossiers/${id}/content-audit/bulk`,
      { method: 'POST' },
    ),
  getContentAudits: (id: number) =>
    request<ContentAudit[]>(`/dossiers/${id}/content-audits`),
  getTopicalGaps: (id: number) =>
    request<TopicalGaps>(`/dossiers/${id}/topical-gaps`),
  getGscPages: (id: number) =>
    request<GscPage[]>(`/dossiers/${id}/gsc-pages`),
  getContentGaps: (id: number) =>
    request<VectorContentGaps>(`/dossiers/${id}/content-gaps`),
  getSerpAio: (id: number) =>
    request<SerpAioData>(`/dossiers/${id}/serp-aio`),
  getCitationPrompts: (id: number) =>
    request<CitationPrompt[]>(`/dossiers/${id}/citation-prompts`),
  generateCitationPrompts: (id: number) =>
    request<{ generated: number; added: number; reason: string }>(`/dossiers/${id}/citation-prompts/generate`, { method: 'POST' }),
  createCitationPrompt: (id: number, prompt_text: string, intent_type?: string) =>
    request<CitationPrompt>(`/dossiers/${id}/citation-prompts`, { method: 'POST', body: { prompt_text, intent_type } }),
  updateCitationPrompt: (dossierId: number, promptId: number, data: { active?: boolean; prompt_text?: string }) =>
    request<CitationPrompt>(`/dossiers/${dossierId}/citation-prompts/${promptId}`, { method: 'PATCH', body: data }),
  deleteCitationPrompt: (dossierId: number, promptId: number) =>
    request<void>(`/dossiers/${dossierId}/citation-prompts/${promptId}`, { method: 'DELETE' }),
}

// Google Search Console
export interface GscProperty {
  siteUrl: string
  permissionLevel: string
}

export interface GscPerformanceRow {
  query?: string
  page?: string
  clicks: number
  impressions: number
  ctr: number
  position: number
}

export interface GscPerformance {
  period_days: number
  start_date: string
  end_date: string
  total_clicks: number
  total_impressions: number
  avg_ctr: number
  avg_position: number
  top_queries: GscPerformanceRow[]
  top_pages: GscPerformanceRow[]
}

export interface GscSitemapContent {
  type: string
  submitted: number
  indexed: number
}

export interface GscSitemap {
  path: string
  last_submitted: string | null
  last_downloaded: string | null
  is_pending: boolean
  is_sitemaps_index: boolean
  type: string
  warnings: number
  errors: number
  contents: GscSitemapContent[]
}

export interface GscInspection {
  inspection_url: string
  verdict: string
  coverage_state: string
  robots_txt_state: string
  indexing_state: string
  last_crawl_time: string | null
  page_fetch_state: string
  google_canonical: string
  user_canonical: string
  crawled_as: string
  mobile_verdict: string
}

export interface GscStatus {
  configured: boolean
  method: 'oauth2' | 'service_account' | null
  oauth_client_configured: boolean
  /** True when the OAuth refresh token is dead and the user must reconnect. */
  reconnect_required: boolean
}

export const gscApi = {
  status: () => request<GscStatus>('/gsc/status'),
  getAuthorizeUrl: () => request<{ url: string }>('/gsc/oauth/authorize'),
  disconnect: () => request<{ disconnected: boolean }>('/gsc/oauth/disconnect', { method: 'DELETE' }),
  listProperties: () => request<GscProperty[]>('/gsc/properties'),
  getPerformance: (siteUrl: string, days = 28) =>
    request<GscPerformance>(`/gsc/performance?site_url=${encodeURIComponent(siteUrl)}&days=${days}`),
  getSitemaps: (siteUrl: string) =>
    request<GscSitemap[]>(`/gsc/sitemaps?site_url=${encodeURIComponent(siteUrl)}`),
  inspectUrl: (siteUrl: string, inspectionUrl: string) =>
    request<GscInspection>('/gsc/inspect', {
      method: 'POST',
      body: { site_url: siteUrl, inspection_url: inspectionUrl },
    }),
}

// App Settings
export interface AppSetting {
  key: string
  value: string | null
  label: string
  description: string | null
  category: string
  is_secret: boolean
  updated_at: string
}

export const appSettingsApi = {
  list: () => request<AppSetting[]>('/settings'),
  get: (key: string) => request<AppSetting>(`/settings/${key}`),
  update: (key: string, value: string | null) =>
    request<AppSetting>(`/settings/${key}`, { method: 'PATCH', body: { value } }),
}

// SEO Scanning
export interface CannibalizationPage {
  page: string
  position: number
  impressions: number
  clicks: number
}

export interface PositionBuckets {
  has_gsc: boolean
  buckets: Record<string, { keywords: number; impressions: number; clicks: number }>
  cannibalization: { query: string; impressions: number; pages: CannibalizationPage[] }[]
}

export interface GscTrendKpi {
  current: number
  previous: number
  delta_pct: number | null
}

export interface GscTrends {
  has_gsc: boolean
  kpis?: {
    clicks: GscTrendKpi
    impressions: GscTrendKpi
    ctr: GscTrendKpi
  }
  timeline?: { week_start: string; clicks: number; impressions: number }[]
  bucket_migration?: {
    current: Record<string, number>
    previous: Record<string, number>
  }
}

// Scan schedules
export interface ScanSchedule {
  scan_type: string
  display_name: string
  category: 'seo' | 'security' | 'gsc' | 'content'
  enabled: boolean
  interval_days: number
  last_triggered_at: string | null
}

export interface ScanScheduleUpdate {
  scan_type: string
  enabled: boolean
  interval_days: number
}

// Unified scan summary
export interface ScanItem {
  id: number
  status: string | null
  created_at: string | null
  completed_at: string | null
  duration_seconds: number | null
  error_message: string | null
  score?: number | null
  // SEO
  total_issues?: number
  critical_count?: number
  warning_count?: number
  progress_percent?: number | null
  progress_detail?: string | null
  // Security
  total_findings?: number
  high_count?: number
  // GSC
  snapshot_date?: string | null
  total_clicks?: number
  total_impressions?: number
  avg_position?: number | null
  avg_ctr?: number | null
  // Content
  page_url?: string
}

export interface ScanTypeSummary {
  type_key: string
  display_name: string
  category: 'seo' | 'security' | 'gsc' | 'content'
  latest: ScanItem | null
  history: ScanItem[]
  total_runs: number
}

// Content audit types
export interface ContentGap {
  type: string
  title: string
  detail: string
  impact: 'hoog' | 'gemiddeld' | 'laag'
  suggestion: string
}

export interface HeadingSuggestion {
  element: string
  current: string
  suggestion: string
  reason: string
}

export interface ClaudeCodeInstruction {
  priority: 'hoog' | 'gemiddeld' | 'laag'
  title: string
  prompt: string
}

export interface ContentAuditResults {
  intent_type: string
  intent_fit: 'goed' | 'matig' | 'slecht'
  intent_fit_reason: string
  overall_score: number
  content_type_detected: string
  gaps: ContentGap[]
  heading_suggestions: HeadingSuggestion[]
  faq_suggestions: string[]
  snippet_opportunity: boolean
  snippet_suggestion: string | null
  missing_topics: string[]
  claude_code_instructions: ClaudeCodeInstruction[]
}

export interface ContentAudit {
  id: number
  page_url: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'failed_fetch'
  overall_score: number | null
  intent_fit: string | null
  word_count: number | null
  gaps_count: number
  created_at: string | null
  completed_at: string | null
  error_message: string | null
  results: ContentAuditResults | null
  gsc_keywords: { query: string; position: number; impressions: number; clicks: number }[] | null
  page_title: string | null
  page_h1: string | null
  page_headings: { level: string; text: string }[] | null
}

export interface TopicalGap {
  type: 'high_volume_low_rank' | 'near_page_one' | 'low_ctr'
  keyword: string
  impressions: number
  position: number
  ctr_pct?: number
  best_page: string
  impact: 'hoog' | 'gemiddeld' | 'laag'
  issue: string
  suggestion: string
}

export interface TopicalGaps {
  has_data: boolean
  computed_at?: string
  gaps?: TopicalGap[]
  coverage_map?: Record<string, { impressions: number; best_position: number; keyword_count: number }>
}

export interface GscPage {
  page: string
  impressions: number
  avg_position: number
  clicks: number
  keyword_count: number
}

// G6: embedding-based content gaps
export interface VectorContentGap {
  query: string
  best_page_url: string | null
  similarity: number
  impressions: number
  position: number
}

export interface VectorContentGaps {
  has_data: boolean
  /** False when no embedding API key is set — a different empty state from
      "configured but not computed yet". */
  configured: boolean
  provider: string
  computed_at?: string | null
  gaps: VectorContentGap[]
  embedding_model?: string
  threshold?: number
  total_analyzed?: number
}

export interface SerpAioResult {
  query: string
  ai_overview_present: boolean
  site_cited: boolean
  cited_urls: string[]
  fetched_at: string | null
}

export interface SerpAioData {
  has_data: boolean
  results: SerpAioResult[]
}

export interface CitationPrompt {
  id: number
  prompt_text: string
  source: 'gsc_generated' | 'manual'
  intent_type: string | null
  active: boolean
  created_at: string | null
}

export interface IndexReportPage {
  url: string
  bucket: string
  status: number | null
  noindex: boolean
  self_canonical: boolean
  coverage_state: string | null
}

export interface IndexReport {
  total: number
  buckets: Record<string, number>
  pages: IndexReportPage[]
  redirected: number
  robots_blocked: number
  redirected_urls: { url: string; to: string }[]
  robots_blocked_urls: string[]
  conflicts: IndexReportPage[]
  has_crawl: boolean
  crawled_at: string | null
}

export interface SeoScan {
  id: number
  target_url: string
  scan_type: string
  status: string
  started_at: string | null
  completed_at: string | null
  duration_seconds: number | null
  score: number | null
  performance_score: number | null
  seo_score: number | null
  accessibility_score: number | null
  best_practices_score: number | null
  a11y_score: number | null
  lcp_ms: number | null
  cls: number | null
  inp_ms: number | null
  pages_crawled: number | null
  max_pages: number
  total_issues: number
  critical_count: number
  warning_count: number
  notice_count: number
  error_message: string | null
  progress_percent: number | null
  progress_detail: string | null
  celery_task_id: string | null
  target_id: number | null
  render_js: boolean
  scan_confidence: string
  blocked: boolean
  pillar_scores: Record<string, number> | null
  created_at: string
}

export interface SeoFinding {
  id: number
  scan_id: number
  category: string
  check: string
  severity: string
  name: string
  description: string | null
  url: string | null
  recommendation: string | null
  value: string | null
  created_at: string
}

export interface SeoStats {
  total_scans: number
  completed_scans: number
  failed_scans: number
  avg_score: number | null
  last_scan_at: string | null
  last_scan_status: string | null
}

export interface SeoScanTypeInfo {
  type: string
  name: string
  description: string
  estimated_duration: string
}

export const seoApi = {
  getScanTypes: () => request<SeoScanTypeInfo[]>('/seo/scan-types'),
  startScan: (data: { target_url: string; scan_type: string; max_pages?: number; dossier_id?: number; render_js?: boolean }) =>
    request<SeoScan>('/seo/scans', { method: 'POST', body: data }),
  listScans: (params?: { status?: string; scan_type?: string; limit?: number; offset?: number }) => {
    const query = new URLSearchParams()
    if (params?.status) query.set('status', params.status)
    if (params?.scan_type) query.set('scan_type', params.scan_type)
    if (params?.limit) query.set('limit', params.limit.toString())
    if (params?.offset) query.set('offset', params.offset.toString())
    return request<SeoScan[]>(`/seo/scans?${query.toString()}`)
  },
  getScan: (id: number) => request<SeoScan>(`/seo/scans/${id}`),
  cancelScan: (id: number) =>
    request<SeoScan>(`/seo/scans/${id}/cancel`, { method: 'POST' }),
  deleteScan: (id: number) =>
    request<void>(`/seo/scans/${id}`, { method: 'DELETE' }),
  clearScans: () =>
    request<void>('/seo/scans?confirm=true', { method: 'DELETE' }),
  getScanFindings: (
    scanId: number,
    params?: { category?: string; severity?: string; limit?: number },
  ) => {
    const query = new URLSearchParams()
    if (params?.category) query.set('category', params.category)
    if (params?.severity) query.set('severity', params.severity)
    if (params?.limit) query.set('limit', params.limit.toString())
    return request<SeoFinding[]>(`/seo/scans/${scanId}/findings?${query.toString()}`)
  },
  getStats: () => request<SeoStats>('/seo/stats'),
}

// Health
export interface HealthCheck {
  status: string
  timestamp: string
  version: string
  services: Record<string, boolean>
}

// ── Scans (SEO + security in one list) ───────────────────────────────────────

/** The five statuses every scan is reported as, regardless of its table. */
export type ScanStatus = 'pending' | 'running' | 'success' | 'failed' | 'skipped'

export interface ScanRow {
  id: number
  kind: 'seo' | 'security'
  scan_type: string
  status: ScanStatus
  /** Value as stored, e.g. "completed" or "skipped_cached". */
  raw_status: string
  /** Human-readable reason, set for the statuses that need one. */
  status_detail: string | null
  dossier_id: number | null
  dossier_name: string | null
  target_url: string
  created_at: string
  started_at: string | null
  completed_at: string | null
  duration_seconds: number | null
  error_message: string | null
  progress_percent: number | null
  progress_detail: string | null
  /** SEO scans only. */
  score: number | null
  issue_count: number
  critical_count: number | null
  /** Security scans only. */
  high_count: number | null
}

export interface ScanHistoryStats {
  total: number
  pending: number
  running: number
  success: number
  failed: number
  skipped: number
}

export const scansApi = {
  list: (params?: {
    kind?: 'seo' | 'security'
    status?: ScanStatus
    dossier_id?: number
    limit?: number
  }) => {
    const query = new URLSearchParams()
    if (params?.kind) query.set('kind', params.kind)
    if (params?.status) query.set('status', params.status)
    if (params?.dossier_id !== undefined) query.set('dossier_id', String(params.dossier_id))
    if (params?.limit) query.set('limit', String(params.limit))
    const qs = query.toString()
    return request<ScanRow[]>(`/scans${qs ? `?${qs}` : ''}`)
  },
  stats: (dossierId?: number) =>
    request<ScanHistoryStats>(
      `/scans/stats${dossierId !== undefined ? `?dossier_id=${dossierId}` : ''}`
    ),
}

export interface SystemOverview {
  services: { database: boolean; redis: boolean }
  images: {
    name: string
    reference: string
    pinned: boolean
    digest: string | null
    digest_updated_at: string | null
  }[]
  scheduled: {
    key: string
    task: string
    schedule: string
    last_run_at: string | null
    last_status: string | null
    stale: boolean
  }[]
  links: Record<string, string>
}

export interface MaintenanceEntry {
  id: number
  event_type: string
  subject: string
  detail: string | null
  old_value: string | null
  new_value: string | null
  occurred_at: string
}

export const systemApi = {
  overview: () => request<SystemOverview>('/system/overview'),
  maintenance: (limit = 50) =>
    request<MaintenanceEntry[]>(`/system/maintenance?limit=${limit}`),
}

export const healthApi = {
  check: () => request<HealthCheck>('/health'),
  live: () => request<{ status: string }>('/health/live'),
  ready: () => request<{ status: string }>('/health/ready'),
}

// Notifications (in-app bell — distinct from Telegram alerts)
export interface AppNotification {
  id: number
  kind: string
  dossier_id: number | null
  title: string
  body: string | null
  meta: {
    snapshot_date?: string
    clicks?: number
    impressions?: number
    position?: number | null
    clicks_delta?: number
    impressions_delta?: number
    first?: boolean
  } | null
  is_read: boolean
  created_at: string
}

// WordPress Integration
export interface WordPressSiteCredentials {
  site_url: string
  api_user: string
  api_password: string
  enabled?: boolean
}

export interface WordPressSiteInfo {
  connected: boolean
  id?: number
  dossier_id?: number
  site_url?: string
  api_user?: string
  api_password?: string
  enabled?: boolean
  last_scanned_at?: string | null
  created_at?: string
}

export interface WordPressPlugin {
  name: string
  plugin_file: string
  version: string
  author: string
  description: string
  active: boolean
  update_available: boolean
  requires_wp: string
  requires_php: string
}

export interface WordPressContentItem {
  id: number
  type: string
  title: string
  url: string
  slug: string
  focus_kw: string
  is_orphan: boolean
}

export interface WordPressScanIssues {
  plugins_with_updates: string[]
  orphan_pages: string[]
  focus_kw_conflicts: Record<string, string[]>
  plugin_count: number
  active_plugin_count: number
}

export interface WordPressScanResults {
  has_data: boolean
  last_scanned_at?: string | null
  scanned_at?: string
  site_info?: { name: string; description: string; url: string; wp_version: string }
  plugins?: WordPressPlugin[]
  active_theme?: { name: string; version: string; author: string; template: string; update_available: boolean } | null
  content?: WordPressContentItem[]
  issues?: WordPressScanIssues
}

export const wordpressApi = {
  get: (dossierId: number) =>
    request<WordPressSiteInfo>(`/dossiers/${dossierId}/wordpress`),
  save: (dossierId: number, payload: WordPressSiteCredentials) =>
    request<WordPressSiteInfo>(`/dossiers/${dossierId}/wordpress`, {
      method: 'POST',
      body: payload,
    }),
  remove: (dossierId: number) =>
    request<void>(`/dossiers/${dossierId}/wordpress`, { method: 'DELETE' }),
  test: (dossierId: number, payload: WordPressSiteCredentials) =>
    request<{ ok: boolean; site_info?: Record<string, string>; error?: string }>(
      `/dossiers/${dossierId}/wordpress/test`,
      { method: 'POST', body: payload }
    ),
  triggerScan: (dossierId: number) =>
    request<{ task_id: string; status: string }>(`/dossiers/${dossierId}/wordpress/scan`, {
      method: 'POST',
    }),
  getScanResults: (dossierId: number) =>
    request<WordPressScanResults>(`/dossiers/${dossierId}/wordpress/scan`),
}

export const notificationsApi = {
  list: (params?: { unread_only?: boolean; limit?: number }) => {
    const q = new URLSearchParams()
    if (params?.unread_only) q.set('unread_only', 'true')
    if (params?.limit) q.set('limit', String(params.limit))
    const qs = q.toString()
    return request<AppNotification[]>(`/notifications/${qs ? `?${qs}` : ''}`)
  },
  unreadCount: () => request<{ count: number }>('/notifications/unread-count'),
  markRead: (id: number) =>
    request<AppNotification>(`/notifications/${id}/read`, { method: 'POST' }),
  markAllRead: () =>
    request<{ count: number }>('/notifications/read-all', { method: 'POST' }),
}
